import React from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import Spinner from "./components/atoms/Spinner";
import { TopNavigation } from "./components/organisms";
import { useLogout, useProfile } from "./hooks/auth";
import { ActivityRecommendationsPage } from "./pages/ActivityRecommendationsPage";
import { ChatPage } from "./pages/ChatPage";
import { DashboardPage } from "./pages/DashboardPage";
import { DataReviewPage } from "./pages/DataReviewPage";
import { GoalsPage } from "./pages/GoalsPage";
import { GuidesPage } from "./pages/GuidesPage";
import { HoverDemo } from "./pages/HoverDemo";
import { LoginPage } from "./pages/LoginPage";
import { ProductDetailPage } from "./pages/ProductDetailPage";
import { ProductsPage } from "./pages/ProductsPage";
import { ProfilePage } from "./pages/ProfilePage";
import { SettingsPage } from "./pages/SettingsPage";
import { TasksPage } from "./pages/TasksPage";
import { WellnessProgramsPage } from "./pages/WellnessProgramsPage";
import { JotaiProvider } from "./providers/JotaiProvider";
import type { User } from "./types/auth";

function App() {
  // Use TanStack Query for authentication state
  const { data: user, isLoading, refetch } = useProfile();

  console.log("User data:", user);
  const logoutMutation = useLogout();

  // Listen for product view events from the web widget
  React.useEffect(() => {
    const handleViewProduct = (
      event: CustomEvent<{ id: string; source_id?: string }>
    ) => {
      const { id, source_id } = event.detail;
      // Navigate to product detail page with source_id as query parameter
      const url = `/products/${id}${source_id ? `?source_id=${source_id}` : ""}`;
      window.location.href = url;
    };

    // Listen for treel.viewProduct events (matches EVENT_NAMES.VIEW_PRODUCT)
    document.addEventListener(
      "treel.viewProduct",
      handleViewProduct as EventListener
    );

    return () => {
      document.removeEventListener(
        "treel.viewProduct",
        handleViewProduct as EventListener
      );
    };
  }, []);

  // Navigation tabs configuration
  const navigationTabs = [
    // {
    //   id: "dashboard",
    //   label: "Dashboard",
    //   path: "/dashboard",
    //   icon: (
    //     <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    //       <path
    //         strokeLinecap="round"
    //         strokeLinejoin="round"
    //         strokeWidth="2"
    //         d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
    //       />
    //     </svg>
    //   ),
    // },

    // {
    //   id: "wellness",
    //   label: "Wellness Programs",
    //   path: "/wellness-programs",
    //   icon: (
    //     <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    //       <path
    //         strokeLinecap="round"
    //         strokeLinejoin="round"
    //         strokeWidth="2"
    //         d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
    //       />
    //     </svg>
    //   ),
    // },

    // {
    //   id: "goals",
    //   label: "Goals",
    //   path: "/goals",
    //   icon: (
    //     <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    //       <path
    //         strokeLinecap="round"
    //         strokeLinejoin="round"
    //         strokeWidth="2"
    //         d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
    //       />
    //     </svg>
    //   ),
    // },

    // {
    //   id: "chat",
    //   label: "Coach Chat",
    //   path: "/chat",
    //   icon: (
    //     <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    //       <path
    //         strokeLinecap="round"
    //         strokeLinejoin="round"
    //         strokeWidth="2"
    //         d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
    //       />
    //     </svg>
    //   ),
    // },

    {
      id: "guides",
      label: "Guides",
      path: "/guides",
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
    },
    {
      id: "products",
      label: "Products",
      path: "/products",
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
          />
        </svg>
      ),
    },
    {
      id: "data-review",
      label: "Data Review",
      path: "/data-review",
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
          />
        </svg>
      ),
    },
    {
      id: "activities",
      label: "Activities",
      path: "/activities",
      icon: (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
          />
        </svg>
      ),
    },
    // {
    //   id: "tasks",
    //   label: "Tasks",
    //   path: "/tasks",
    //   icon: (
    //     <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    //       <path
    //         strokeLinecap="round"
    //         strokeLinejoin="round"
    //         strokeWidth="2"
    //         d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
    //       />
    //     </svg>
    //   ),
    // },
    // {
    //   id: "settings",
    //   label: "Settings",
    //   path: "/settings",
    //   icon: (
    //     <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    //       <path
    //         strokeLinecap="round"
    //         strokeLinejoin="round"
    //         strokeWidth="2"
    //         d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
    //       />
    //       <path
    //         strokeLinecap="round"
    //         strokeLinejoin="round"
    //         strokeWidth="2"
    //         d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    //       />
    //     </svg>
    //   ),
    // },
  ];

  const handleLoginSuccess = (_loggedInUser: User, _token: string) => {
    // The profile query will automatically update via TanStack Query
    void refetch();
  };

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4">
            <Spinner size="xl" variant="primary" />
          </div>
          <p className="text-text-muted">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <JotaiProvider>
      <Router>
        <div className="h-screen bg-bg-primary flex flex-col">
          {/* Show combined navigation only when user is authenticated */}
          {user && (
            <TopNavigation
              tabs={navigationTabs}
              user={user}
              onLogout={handleLogout}
            />
          )}

          <div className="flex-1 flex flex-col">
            <Routes>
              <Route
                path="/login"
                element={
                  user ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <LoginPage onLoginSuccess={handleLoginSuccess} />
                  )
                }
              />
              <Route
                path="/dashboard"
                element={
                  user ? <DashboardPage /> : <Navigate to="/login" replace />
                }
              />
              <Route
                path="/chat"
                element={user ? <ChatPage /> : <Navigate to="/login" replace />}
              />
              <Route
                path="/goals"
                element={
                  user ? <GoalsPage /> : <Navigate to="/login" replace />
                }
              />
              <Route
                path="/guides"
                element={
                  user ? <GuidesPage /> : <Navigate to="/login" replace />
                }
              />
              <Route
                path="/tasks"
                element={
                  user ? <TasksPage /> : <Navigate to="/login" replace />
                }
              />
              <Route
                path="/profile"
                element={
                  user ? <ProfilePage /> : <Navigate to="/login" replace />
                }
              />
              <Route
                path="/settings"
                element={
                  user ? <SettingsPage /> : <Navigate to="/login" replace />
                }
              />
              <Route
                path="/products"
                element={
                  user ? <ProductsPage /> : <Navigate to="/login" replace />
                }
              />
              <Route
                path="/products/:id"
                element={
                  user ? (
                    <ProductDetailPage />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/data-review"
                element={
                  user ? <DataReviewPage /> : <Navigate to="/login" replace />
                }
              />
              <Route
                path="/activities"
                element={
                  user ? (
                    <ActivityRecommendationsPage />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route path="/showcase" element={<HoverDemo />} />
              <Route
                path="/wellness-programs"
                element={
                  user ? (
                    <WellnessProgramsPage />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              <Route
                path="/"
                element={
                  <Navigate to={user ? "/dashboard" : "/login"} replace />
                }
              />
              {/* Catch-all route for 404 pages */}
              <Route
                path="*"
                element={
                  <div className="flex-1 flex flex-col items-center justify-center">
                    <h1 className="text-4xl font-bold text-text-primary mb-4">
                      404
                    </h1>
                    <p className="text-text-muted mb-6">Page not found</p>
                    <button
                      onClick={() => (window.location.href = "/")}
                      className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors"
                    >
                      Go to Home
                    </button>
                  </div>
                }
              />
            </Routes>
          </div>
        </div>
      </Router>
    </JotaiProvider>
  );
}

export default App;
